import React, { useState, useEffect } from 'react';
import { Plus, Loader, Eye, EyeOff } from 'lucide-react';
import { useAgendamentos } from './useAgendamentos';
import { useAuth } from '../../contexts/AuthContext';
import CalendarioNavegacao from './CalendarioNavegacao';
import Filtros from './Filtros';
import TimelineAgendamentos from './TimelineAgendamentos';
import ListaMobile from './ListaMobile';
import ModalAgendamento from './ModalAgendamento';

const Agendamentos = () => {
  const {
    dataSelecionada,
    setDataSelecionada,
    showModal,
    setShowModal,
    agendamentoSelecionado,
    filtroProfissional,
    setFiltroProfissional,
    filtroServico,
    setFiltroServico,
    agendamentos,
    formData,
    setFormData,
    loading,
    profissionais,
    funcionarios,
    clientes,
    servicos,
    abrirNovoAgendamento,
    abrirEditarAgendamento,
    salvarAgendamento,
    excluirAgendamento,
    isFuncionario,
    horarioInicial,
    setHorarioInicial,
    refreshAgendamentos,
    carregarAgendamentos // 🔥 ADICIONAR ESTE
  } = useAgendamentos();

  const { usuario, temPermissao } = useAuth();
  
  // 🔥 ESTADO PARA MOSTRAR/OCULTAR CONCLUÍDOS
  const [mostrarConcluidos, setMostrarConcluidos] = useState(true);

  // 🔥 FILTRAR AGENDAMENTOS PARA EXIBIÇÃO
  const agendamentosExibidos = mostrarConcluidos 
    ? agendamentos 
    : agendamentos.filter(a => a.status !== 'concluido');

  // 🔥 FORÇAR RECARREGAMENTO
  const forcarRecarregamento = async () => {
    console.log('🔄 Forçando recarregamento...');
    await refreshAgendamentos();
    // Recarregar novamente para garantir
    setTimeout(async () => {
      await refreshAgendamentos();
    }, 500);
  };

  // 🔥 LISTENER PARA EVENTOS DE RECARREGAMENTO
  useEffect(() => {
    const handleRefresh = () => {
      console.log('🔄 Evento de refresh recebido, recarregando...');
      forcarRecarregamento();
    };

    window.addEventListener('refreshAgendamentos', handleRefresh);
    window.addEventListener('agendamentoConcluido', handleRefresh);
    window.addEventListener('agendamentoAtualizado', handleRefresh);
    window.addEventListener('novoAgendamento', handleRefresh);
    window.addEventListener('agendamentoRemovido', handleRefresh);

    return () => {
      window.removeEventListener('refreshAgendamentos', handleRefresh);
      window.removeEventListener('agendamentoConcluido', handleRefresh);
      window.removeEventListener('agendamentoAtualizado', handleRefresh);
      window.removeEventListener('novoAgendamento', handleRefresh);
      window.removeEventListener('agendamentoRemovido', handleRefresh);
    };
  }, []);

  useEffect(() => {
    console.log('🎯 Agendamentos no componente:', {
      total: agendamentos.length,
      concluidos: agendamentos.filter(a => a.status === 'concluido').length,
      isFuncionario,
      profissionais: profissionais.map(p => p.nome),
      horarioInicial
    });
  }, [agendamentos, isFuncionario, profissionais, horarioInicial]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Carregando agendamentos...</span>
      </div>
    );
  }

  // 🔥 CONTAR AGENDAMENTOS CONCLUÍDOS
  const totalConcluidos = agendamentos.filter(a => a.status === 'concluido').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Agendamentos</h1>
          <p className="text-gray-600 mt-1">
            {isFuncionario 
              ? 'Seus agendamentos' 
              : 'Gerencie os horários e serviços agendados'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* 🔥 BOTÃO PARA MOSTRAR/OCULTAR CONCLUÍDOS */}
          <button
            onClick={() => setMostrarConcluidos(!mostrarConcluidos)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
              mostrarConcluidos 
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {mostrarConcluidos ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {mostrarConcluidos ? 'Ocultar Concluídos' : 'Mostrar Concluídos'}
            <span className="ml-1 text-xs bg-gray-200 px-2 py-0.5 rounded-full">
              {totalConcluidos}
            </span>
          </button>
          
          {temPermissao('agendamentos', 'editar') && (
            <button 
              onClick={() => abrirNovoAgendamento()}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Novo Agendamento
            </button>
          )}
        </div>
      </div>

      {/* Filtros e Navegação */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <CalendarioNavegacao 
          dataSelecionada={dataSelecionada}
          setDataSelecionada={setDataSelecionada}
        />
        
        <Filtros 
          filtroProfissional={filtroProfissional}
          setFiltroProfissional={setFiltroProfissional}
          filtroServico={filtroServico}
          setFiltroServico={setFiltroServico}
          agendamentos={agendamentosExibidos}
          dataSelecionada={dataSelecionada}
          profissionais={profissionais}
          servicos={servicos}
        />
      </div>

      {/* Timeline de Agendamentos */}
      <TimelineAgendamentos 
        dataSelecionada={dataSelecionada}
        agendamentos={agendamentosExibidos}
        onEditar={temPermissao('agendamentos', 'editar') ? abrirEditarAgendamento : null}
        onNovo={temPermissao('agendamentos', 'editar') ? abrirNovoAgendamento : null}
        profissionais={profissionais}
        filtroProfissional={filtroProfissional}
        filtroServico={filtroServico}
      />

      {/* Lista de Agendamentos do Dia (para mobile) */}
      <ListaMobile 
        dataSelecionada={dataSelecionada}
        agendamentos={agendamentosExibidos}
        onEditar={temPermissao('agendamentos', 'editar') ? abrirEditarAgendamento : null}
        profissionais={profissionais}
      />

      {/* Modal de Agendamento */}
      {temPermissao('agendamentos', 'editar') && (
        <ModalAgendamento
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setHorarioInicial(null);
          }}
          agendamentoSelecionado={agendamentoSelecionado}
          formData={formData}
          setFormData={setFormData}
          onSalvar={salvarAgendamento}
          onExcluir={temPermissao('agendamentos', 'excluir') ? excluirAgendamento : null}
          profissionais={profissionais}
          servicos={servicos}
          clientes={clientes}
          horarioInicial={horarioInicial}
          onRefreshData={forcarRecarregamento} // 🔥 PASSAR A FUNÇÃO DE FORÇAR RECARREGAMENTO
        />
      )}
    </div>
  );
};

export default Agendamentos;
